import { useCallback, useEffect, useState } from "react";
import { Plus, ClipboardText } from "@phosphor-icons/react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";

const KIND_LABEL = { yesno: "Yes / No tap", choice: "Pick one", text: "Short answer" };

export const TASK_CODE_LABELS = {
  "115": "Meal Preparation", "116": "Housework", "117": "Managing Finances", "118": "Managing Medications",
  "119": "Shopping", "120": "Transportation", "122": "Hygiene", "123": "Dressing – Upper Body",
  "124": "Dressing – Lower Body", "125": "Locomotion", "126": "Transfers", "127": "Toilet Use",
  "128": "Bed Mobility", "129": "Eating", "130": "Bladder Incontinence", "131": "Bowel Incontinence",
  "132": "Other Personal Care", "134": "Bathing – Wash-Up", "135": "Bathing – Shower", "137": "Lotion",
  "138": "Laundry", "139": "Reading/Writing", "140": "Supervision/Cueing", "141": "Incontinence Care", "145": "Stairs",
};

export function WrapQuestionBank() {
  const [bank, setBank] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [kind, setKind] = useState("yesno");
  const [category, setCategory] = useState("");
  const [options, setOptions] = useState("");
  const [taskCode, setTaskCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/wrap-questions").then((r) => setBank(r.data.bank)).catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async (q) => {
    try {
      await api.put(`/admin/wrap-questions/${q.id}`, { active: !q.active });
      setBank((cur) => cur.map((x) => (x.id === q.id ? { ...x, active: !x.active } : x)));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const add = async () => {
    if (!prompt.trim()) { toast.error("Write the question first"); return; }
    setBusy(true);
    try {
      const opts = kind === "choice" ? options.split(",").map((o) => o.trim()).filter(Boolean) : [];
      await api.post("/admin/wrap-questions", { prompt: prompt.trim(), kind, category: category.trim() || (taskCode ? `Task ${taskCode}` : "Custom"), options: opts, task_code: taskCode });
      toast.success("Question added to the bank");
      setPrompt(""); setOptions(""); setCategory(""); setTaskCode("");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!bank) return null;

  return (
    <div className="mt-6 rounded-xl border border-stone-200 bg-white p-6 shadow-sm" data-testid="wrap-bank-card">
      <div className="flex items-center gap-2">
        <ClipboardText size={18} className="text-primary" />
        <p className="font-heading text-sm font-semibold text-stone-900">Wrap-up question bank</p>
      </div>
      <p className="mt-1 text-xs text-stone-500">
        The small things to monitor — food, hydration, bowel habits, weight, turning, naps, showers. Questions tied to a
        task code appear automatically on the wrap-up of every client whose care plan has that code (the office can turn
        one off per client on the client's page). Untied questions are assigned per client by hand.
      </p>

      <ul className="mt-4 space-y-2" data-testid="wrap-bank-list">
        {bank.map((q) => (
          <li key={q.id} className="flex flex-wrap items-center gap-2 rounded-lg border border-stone-100 px-3 py-2" data-testid={`wrap-bank-${q.id}`}>
            <span className="rounded-full bg-stone-100 px-2 py-0.5 text-[10px] font-semibold text-stone-500">{q.category}</span>
            {q.task_code && (
              <span className="rounded-full border border-[#4A235A]/30 bg-[#4A235A]/5 px-2 py-0.5 text-[10px] font-semibold text-[#4A235A]"
                data-testid={`wrap-bank-code-${q.id}`}>auto · {TASK_CODE_LABELS[String(q.task_code)] || q.task_code}</span>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-sm text-stone-800">{q.prompt}</p>
              <p className="text-[11px] text-stone-400">{KIND_LABEL[q.kind]}{q.options?.length ? ` — ${q.options.join(" · ")}` : ""}</p>
            </div>
            <button onClick={() => toggle(q)}
              className={`rounded-full px-3 py-1 text-[11px] font-semibold transition-colors ${q.active ? "bg-emerald-600 text-white" : "bg-stone-100 text-stone-500 hover:bg-stone-200"}`}
              data-testid={`wrap-bank-toggle-${q.id}`}>
              {q.active ? "On" : "Off"}
            </button>
          </li>
        ))}
      </ul>

      <div className="mt-4 rounded-lg border border-dashed border-stone-300 p-3">
        <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Add your own</p>
        <div className="mt-2 grid gap-2 sm:grid-cols-2">
          <input value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="The question, e.g. 'Did they use their walker today?'"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm sm:col-span-2" data-testid="wrap-bank-new-prompt" />
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Mobility)"
            className="rounded-lg border border-stone-200 px-3 py-2 text-sm" data-testid="wrap-bank-new-category" />
          <select value={kind} onChange={(e) => setKind(e.target.value)}
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="wrap-bank-new-kind">
            <option value="yesno">Yes / No tap</option>
            <option value="choice">Pick one (options)</option>
            <option value="text">Short answer</option>
          </select>
          <select value={taskCode} onChange={(e) => setTaskCode(e.target.value)}
            className="rounded-lg border border-stone-200 bg-white px-3 py-2 text-sm" data-testid="wrap-bank-new-taskcode">
            <option value="">No task code (assign by hand)</option>
            {Object.entries(TASK_CODE_LABELS).map(([c, l]) => (
              <option key={c} value={c}>{c} — {l} (auto by care plan)</option>
            ))}
          </select>
          {kind === "choice" && (
            <input value={options} onChange={(e) => setOptions(e.target.value)} placeholder="Options, comma separated"
              className="rounded-lg border border-stone-200 px-3 py-2 text-sm sm:col-span-2" data-testid="wrap-bank-new-options" />
          )}
        </div>
        <button onClick={add} disabled={busy}
          className="mt-2 inline-flex items-center gap-1 rounded-full bg-[#4A235A] px-4 py-1.5 text-xs font-semibold text-white hover:bg-stone-900 disabled:opacity-50"
          data-testid="wrap-bank-add">
          <Plus size={12} weight="bold" /> Add to bank
        </button>
      </div>
    </div>
  );
}
